import React, { useState, useEffect } from 'react';
import { Users, Clock, Eye, Activity, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const EVENT_LABELS = {
  login: { label: 'Login', color: 'bg-emerald-100 text-emerald-700' },
  login_google: { label: 'Google Login', color: 'bg-sky-100 text-sky-700' },
  logout: { label: 'Logout', color: 'bg-gray-100 text-gray-600' },
  page_view: { label: 'Page View', color: 'bg-indigo-100 text-indigo-700' },
};

function formatDwell(seconds) {
  if (!seconds || seconds === 0) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export default function ActivityLog() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('trove_helix_token');
        const res = await fetch(`${API_BASE}/api/admin/auth/activity`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  if (user?.role !== 'admin') {
    return (
      <div className="p-6 text-center text-gray-500">
        <p className="text-lg font-medium">Access Denied</p>
        <p className="text-sm mt-2">Only administrators can view the activity log.</p>
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#FF8C42] border-t-transparent rounded-full" />
    </div>
  );

  const summaries = data?.user_summaries || [];
  const activities = data?.recent_activity || [];

  const totalUsers = summaries.length;
  const totalViews = activities.filter(a => a.event === 'page_view').length;
  const totalDwell = summaries.reduce((s, u) => s + (u.total_dwell_seconds || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="activity-log-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Activity Log</h1>
        <p className="text-sm text-gray-500 mt-1">Track who has accessed <span style={{ fontFamily: 'Georgia, serif' }}>trove helix<span className="text-[#FF8C42]">.</span></span> and their engagement</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-indigo-600" />
              <p className="text-xs text-gray-500 uppercase">Unique Visitors</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="w-4 h-4 text-sky-600" />
              <p className="text-xs text-gray-500 uppercase">Page Views</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalViews}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-emerald-600" />
              <p className="text-xs text-gray-500 uppercase">Total Dwell Time</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatDwell(totalDwell)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-orange-600" />
              <p className="text-xs text-gray-500 uppercase">Total Events</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* User Summaries */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Visitor Summary</CardTitle>
          <CardDescription>Engagement per user — who viewed what and how long</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Pages Viewed</TableHead>
                <TableHead className="text-right">Total Events</TableHead>
                <TableHead className="text-right">Dwell Time</TableHead>
                <TableHead>Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">No visitors yet</TableCell>
                </TableRow>
              ) : (
                summaries.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{s.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{s.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(s.pages_viewed || []).filter(Boolean).map((p, j) => (
                          <Badge key={j} variant="outline" className="text-xs">{p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{s.total_events}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">{formatDwell(s.total_dwell_seconds)}</TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {s.last_active ? new Date(s.last_active).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' }) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
          <CardDescription>Last 100 events</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Page</TableHead>
                <TableHead className="text-right">Dwell</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.slice(0, 100).map((a, i) => {
                const evt = EVENT_LABELS[a.event] || { label: a.event, color: 'bg-gray-100 text-gray-700' };
                return (
                  <TableRow key={i}>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(a.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' })}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{a.name || a.email}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${evt.color} text-xs`}>{evt.label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{a.page || '-'}</TableCell>
                    <TableCell className="text-right text-sm">{formatDwell(a.dwell_seconds)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
